import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Pencil, Download, RotateCcw } from "lucide-react";
import Logo from "@/components/Logo";
import { getContent, saveContent, resetContent, publishRemoteContent } from "@/lib/staticData";

const DASH_PASSWORD = "Strovyn@2026";

const inputCls =
  "w-full border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-accent";

function Field({ label, note, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      {children}
      {note && <span className="mt-1 block text-xs text-muted-foreground">{note}</span>}
    </label>
  );
}

const FILE_SPECS = [
  ["jobs.js", "jobs", (c) => c.roles],
  ["gallery.js", "gallery", (c) => c.gallery],
  ["testimonials.js", "testimonials", (c) => c.testimonials],
  ["partners.js", "partners", (c) => c.partners],
  ["settings.js", "siteSettings", (c) => c.settings],
];

function downloadFile(filename, varName, data) {
  const text = `// Updated via the /buildweb dashboard — replace this file in frontend/src/content/\n\nexport const ${varName} = ${JSON.stringify(data, null, 2)};\n`;
  const blob = new Blob([text], { type: "text/javascript" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

const TABS = [
  { slug: "jobs", label: "Job Openings" },
  { slug: "gallery", label: "Gallery Photos" },
  { slug: "testimonials", label: "Word of Mouth" },
  { slug: "partners", label: "Partner Logos" },
  { slug: "settings", label: "Site Details" },
  { slug: "publish", label: "Publish" },
];

function JobsEditor({ content, update }) {
  const empty = { id: "", title: "", type: "Full-time", location: "Adelaide, SA", summary: "" };
  const [form, setForm] = useState(empty);
  const editing = !!form.id;

  const submit = (e) => {
    e.preventDefault();
    const roles = editing
      ? content.roles.map((r) => (r.id === form.id ? form : r))
      : [...content.roles, { ...form, id: `job-${Date.now()}` }];
    update({ roles });
    setForm(empty);
    toast.success(editing ? "Job updated" : "Job opening added");
  };

  return (
    <div>
      <form onSubmit={submit} className="grid gap-4 border border-border bg-card p-6" data-testid="static-jobs-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Job title">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls} data-testid="static-job-title-input" />
          </Field>
          <Field label="Employment type">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              {["Full-time", "Part-time", "Casual", "Contract", "Apprenticeship"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Location">
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} />
        </Field>
        <Field label="Short description">
          <textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
            className={`${inputCls} resize-none`} />
        </Field>
        <div className="flex gap-3">
          <button type="submit" data-testid="static-job-save-button"
            className="bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white">
            {editing ? "Save Changes" : "Add Job Opening"}
          </button>
          {editing && (
            <button type="button" onClick={() => setForm(empty)}
              className="border border-border px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Cancel
            </button>
          )}
        </div>
      </form>
      <ItemList
        items={content.roles}
        title={(r) => r.title}
        sub={(r) => `${r.type} · ${r.location}`}
        onEdit={(r) => setForm(r)}
        onRemove={(r) => update({ roles: content.roles.filter((x) => x.id !== r.id) })}
      />
    </div>
  );
}

function GalleryEditor({ content, update }) {
  const empty = { id: "", src: "", title: "", cat: "residential", meta: "", span: "" };
  const [form, setForm] = useState(empty);
  const editing = !!form.id;

  const pickFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 1400;
        const scale = Math.min(1, max / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        setForm((f) => ({ ...f, src: canvas.toDataURL("image/jpeg", 0.82) }));
        toast.success("Photo ready — save to add it");
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.src) return toast.error("Choose a photo or paste an image link first");
    const gallery = editing
      ? content.gallery.map((g) => (g.id === form.id ? form : g))
      : [...content.gallery, { ...form, id: `photo-${Date.now()}` }];
    update({ gallery });
    setForm(empty);
    e.target.reset?.();
    toast.success(editing ? "Photo updated" : "Photo added to your gallery");
  };

  return (
    <div>
      <form onSubmit={submit} className="grid gap-4 border border-border bg-card p-6" data-testid="static-gallery-form">
        <Field label="Photo from this device" note="Resized automatically so the site stays fast. For publishing, also keep the original file.">
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => pickFile(e.target.files?.[0])}
            data-testid="static-gallery-file-input"
            className="w-full text-sm file:mr-4 file:border-0 file:bg-accent file:px-4 file:py-2.5 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-white" />
        </Field>
        <Field label="…or paste an image link">
          <input value={form.src.startsWith("data:") ? "" : form.src}
            onChange={(e) => setForm({ ...form, src: e.target.value })}
            placeholder="https://…" className={inputCls} data-testid="static-gallery-src-input" />
        </Field>
        {form.src && (
          <img src={form.src} alt="Preview" className="h-24 w-36 border border-border object-cover" />
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Photo title">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Type of job">
            <select value={form.cat} onChange={(e) => setForm({ ...form, cat: e.target.value })} className={inputCls}>
              <option value="residential">Residential</option>
              <option value="exterior">Exterior</option>
              <option value="commercial">Commercial</option>
              <option value="detail">Detail</option>
            </select>
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Short caption">
            <input value={form.meta} onChange={(e) => setForm({ ...form, meta: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Tile size">
            <select value={form.span} onChange={(e) => setForm({ ...form, span: e.target.value })} className={inputCls}>
              <option value="">Normal</option>
              <option value="col-span-2">Wide</option>
              <option value="col-span-2 row-span-2">Big feature tile</option>
            </select>
          </Field>
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="static-gallery-save-button"
            className="bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white">
            {editing ? "Save Changes" : "Add Photo"}
          </button>
          {editing && (
            <button type="button" onClick={() => setForm(empty)}
              className="border border-border px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Cancel
            </button>
          )}
        </div>
      </form>
      <ItemList
        items={content.gallery}
        thumb={(g) => g.src}
        title={(g) => g.title}
        sub={(g) => `${g.cat}${g.meta ? ` · ${g.meta}` : ""}`}
        onEdit={(g) => setForm(g)}
        onRemove={(g) => update({ gallery: content.gallery.filter((x) => !(x.id ? x.id === g.id : x === g)) })}
      />
    </div>
  );
}

function TestimonialsEditor({ content, update }) {
  const empty = { id: "", text: "", author: "", context: "" };
  const [form, setForm] = useState(empty);
  const editing = !!form.id;

  const submit = (e) => {
    e.preventDefault();
    const testimonials = editing
      ? content.testimonials.map((t) => (t.id === form.id ? form : t))
      : [...content.testimonials, { ...form, id: `quote-${Date.now()}` }];
    update({ testimonials });
    setForm(empty);
    toast.success(editing ? "Quote updated" : "Quote added");
  };

  return (
    <div>
      <form onSubmit={submit} className="grid gap-4 border border-border bg-card p-6" data-testid="static-testimonials-form">
        <Field label="What the customer said">
          <textarea required rows={3} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
            className={`${inputCls} resize-none`} data-testid="static-testimonial-text-input" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Customer name">
            <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} />
          </Field>
          <Field label="What job was it?">
            <input value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className={inputCls} />
          </Field>
        </div>
        <div className="flex gap-3">
          <button type="submit" className="bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white">
            {editing ? "Save Changes" : "Add Quote"}
          </button>
          {editing && (
            <button type="button" onClick={() => setForm(empty)}
              className="border border-border px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Cancel
            </button>
          )}
        </div>
      </form>
      <ItemList
        items={content.testimonials}
        title={(t) => `"${t.text.slice(0, 60)}${t.text.length > 60 ? "…" : ""}"`}
        sub={(t) => `${t.author}${t.context ? ` — ${t.context}` : ""}`}
        onEdit={(t) => setForm(t)}
        onRemove={(t) => update({ testimonials: content.testimonials.filter((x) => !(x.id ? x.id === t.id : x === t)) })}
      />
    </div>
  );
}

function PartnersEditor({ content, update }) {
  const [name, setName] = useState("");
  const add = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    update({ partners: [...content.partners, name.trim()] });
    setName("");
    toast.success("Partner added");
  };
  return (
    <div>
      <form onSubmit={add} className="flex gap-3 border border-border bg-card p-6" data-testid="static-partners-form">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Company name"
          className={inputCls} data-testid="static-partner-name-input" />
        <button type="submit" className="shrink-0 bg-accent px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white">
          Add
        </button>
      </form>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="static-partners-list">
        {content.partners.map((p, i) => (
          <span key={`${p}-${i}`} className="flex items-center gap-2 border border-border bg-card px-4 py-2 text-sm">
            {p}
            <button onClick={() => update({ partners: content.partners.filter((_, j) => j !== i) })}
              aria-label={`Remove ${p}`} className="text-muted-foreground hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

function SettingsEditor({ content, update }) {
  const [form, setForm] = useState(content.settings);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    update({ settings: form });
    toast.success("Site details saved");
  };
  return (
    <form onSubmit={submit} className="grid gap-4 border border-border bg-card p-6" data-testid="static-settings-form">
      <Field label="Your email (shown on the website)">
        <input type="email" value={form.notification_email} onChange={set("notification_email")} className={inputCls} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Phone number 1">
          <input value={form.phone1} onChange={set("phone1")} className={inputCls} />
        </Field>
        <Field label="Phone number 2">
          <input value={form.phone2} onChange={set("phone2")} className={inputCls} />
        </Field>
      </div>
      <Field label="Location shown on site">
        <input value={form.location} onChange={set("location")} className={inputCls} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Instagram"><input value={form.instagram} onChange={set("instagram")} className={inputCls} /></Field>
        <Field label="Facebook"><input value={form.facebook} onChange={set("facebook")} className={inputCls} /></Field>
        <Field label="TikTok"><input value={form.tiktok} onChange={set("tiktok")} className={inputCls} /></Field>
        <Field label="LinkedIn"><input value={form.linkedin} onChange={set("linkedin")} className={inputCls} /></Field>
      </div>
      <button type="submit" data-testid="static-settings-save-button"
        className="bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white">
        Save Site Details
      </button>
    </form>
  );
}

function ItemList({ items, title, sub, thumb, onEdit, onRemove }) {
  return (
    <div className="mt-4 space-y-3" data-testid="static-admin-list">
      {items.length === 0 && (
        <p className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing here yet.
        </p>
      )}
      {items.map((item, i) => (
        <div key={item.id || i} className="flex items-start gap-4 border border-border bg-card p-4">
          {thumb && <img src={thumb(item)} alt="" className="h-16 w-24 shrink-0 border border-border object-cover" />}
          <div className="flex-1">
            <p className="font-serif text-base uppercase tracking-tight">{title(item)}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{sub(item)}</p>
          </div>
          <button onClick={() => onEdit(item)} data-testid={`static-edit-${i}`}
            className="flex shrink-0 items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:border-accent hover:text-accent">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
          <button onClick={() => onRemove(item)} data-testid={`static-remove-${i}`}
            className="flex shrink-0 items-center gap-2 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:border-destructive hover:text-destructive">
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </button>
        </div>
      ))}
    </div>
  );
}

export default function StaticAdmin() {
  const [authed, setAuthed] = useState(() => localStorage.getItem("strovyn-static-admin") === "1");
  const [pw, setPw] = useState("");
  const [content, setContentState] = useState(getContent);
  const [tab, setTab] = useState("jobs");
  const [publishing, setPublishing] = useState(false);

  const update = (patch) => {
    const next = { ...content, ...patch };
    setContentState(next);
    try {
      saveContent(next);
    } catch {
      toast.error("Couldn't save on this device — photos may be too large");
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await publishRemoteContent(content, localStorage.getItem("strovyn-static-pw") || "");
      toast.success("Published — your changes are now live for everyone");
    } catch (e) {
      toast.error(`${e.message}. Publishing works once the site is on Netlify — meanwhile use the file downloads below.`);
    } finally {
      setPublishing(false);
    }
  };

  const doLogin = (e) => {
    e.preventDefault();
    if (pw === DASH_PASSWORD) {
      localStorage.setItem("strovyn-static-admin", "1");
      localStorage.setItem("strovyn-static-pw", pw);
      setAuthed(true);
    } else {
      toast.error("Wrong password");
    }
  };

  if (!authed) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4">
        <form onSubmit={doLogin} className="w-full max-w-sm border border-border bg-card p-8" data-testid="static-admin-login-form">
          <Logo />
          <h1 className="mt-6 font-serif text-3xl uppercase tracking-tight">Owner Login</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Your private editing page for this website.
          </p>
          <div className="mt-6 space-y-4">
            <Field label="Password">
              <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)}
                className={inputCls} data-testid="static-admin-password-input" />
            </Field>
            <button type="submit" data-testid="static-admin-login-button"
              className="w-full bg-accent px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white">
              Sign In
            </button>
            <a href="/" className="block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-accent">
              Back to website
            </a>
          </div>
        </form>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-screen max-w-5xl px-4 py-10 sm:px-8" data-testid="static-admin-dashboard">
      <div className="flex items-center justify-between">
        <Logo />
        <div className="flex gap-2">
          <a href="/" className="border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent">
            View Website
          </a>
          <button
            onClick={() => { localStorage.removeItem("strovyn-static-admin"); setAuthed(false); }}
            className="border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent"
            data-testid="static-admin-logout-button"
          >
            Log out
          </button>
        </div>
      </div>

      <h1 className="mt-8 font-serif text-3xl uppercase tracking-tight sm:text-4xl">Website Editor</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Changes you make here show instantly on the website <strong>on this device</strong>.
        When you're happy, go to the <strong>Publish</strong> tab to download your updated
        files and make the changes live for everyone.
      </p>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-border pb-px">
        {TABS.map((t) => (
          <button
            key={t.slug}
            data-testid={`static-admin-tab-${t.slug}`}
            onClick={() => setTab(t.slug)}
            className={`px-4 py-2.5 text-sm transition-colors ${
              tab === t.slug ? "border-b-2 border-accent font-semibold text-accent" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "jobs" && <JobsEditor content={content} update={update} />}
        {tab === "gallery" && <GalleryEditor content={content} update={update} />}
        {tab === "testimonials" && <TestimonialsEditor content={content} update={update} />}
        {tab === "partners" && <PartnersEditor content={content} update={update} />}
        {tab === "settings" && <SettingsEditor content={content} update={update} />}
        {tab === "publish" && (
          <div className="space-y-6" data-testid="static-publish-panel">
            <div className="border border-accent bg-card p-6">
              <h2 className="font-serif text-xl uppercase tracking-tight">Publish now — one tap</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Makes everything you've edited here live for every visitor, straight away.
                Works once your site is deployed on Netlify.
              </p>
              <button
                onClick={publish}
                disabled={publishing}
                data-testid="static-publish-now-button"
                className="mt-4 bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-opacity disabled:opacity-50"
              >
                {publishing ? "Publishing..." : "Publish Now — Make Live For Everyone"}
              </button>
            </div>
            <div className="border border-border bg-card p-6">
              <h2 className="font-serif text-xl uppercase tracking-tight">The manual way (backup)</h2>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
                <li>Download the files you changed below.</li>
                <li>In VS Code, replace the matching files inside <code className="text-accent">frontend/src/content/</code> with the downloaded ones.</li>
                <li>Run <code className="text-accent">yarn export:netlify</code>.</li>
                <li>Drag the new <code className="text-accent">strovyn-netlify</code> folder onto Netlify again — your changes are live.</li>
              </ol>
            </div>
            <div className="flex flex-wrap gap-3">
              {FILE_SPECS.map(([filename, varName, pick]) => (
                <button
                  key={filename}
                  onClick={() => downloadFile(filename, varName, pick(content))}
                  data-testid={`static-download-${filename}`}
                  className="flex items-center gap-2 border border-border bg-card px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-accent hover:text-accent"
                >
                  <Download className="h-4 w-4" /> {filename}
                </button>
              ))}
            </div>
            <button
              onClick={() => {
                resetContent();
                setContentState(getContent());
                toast.success("All your edits cleared — back to the published content");
              }}
              data-testid="static-reset-button"
              className="flex items-center gap-2 border border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground hover:border-destructive hover:text-destructive"
            >
              <RotateCcw className="h-4 w-4" /> Undo all my edits (back to published version)
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
